const ZOLA_PDP_INITIAL_COLOR_KEY = 'ZOLA_PDP_INITIAL_COLOR_KEY';
const ZOLA_PDP_FILTERED_SHAPE_PREVIEW = 'ZOLA_PDP_FILTERED_SHAPE_PREVIEW';

/**
 * Stores a theme color in session storage for the specified card suite UUIDs.
 * Used to show a previously selected color when a card PDP is loaded.
 *
 * @param suiteUUIDs A suite UUID or array of suite UUIDs to save the color for.
 * @param color The theme color to select when loading a suite's PDP.
 */
export const storeInitialColor = (suiteUUIDs: string | string[], color: string) => {
  if (!window?.sessionStorage || !suiteUUIDs || !color) return;

  const suiteUUIDsArray = Array.isArray(suiteUUIDs) ? suiteUUIDs : [suiteUUIDs];

  const newStoredColors = suiteUUIDsArray.reduce((acc, suiteUUID) => {
    if (!suiteUUID) return acc;
    return {
      ...acc,
      [suiteUUID]: color,
    };
  }, {});

  const previousStoredColors = JSON.parse(
    window.sessionStorage.getItem(ZOLA_PDP_INITIAL_COLOR_KEY) || '{}'
  );
  window.sessionStorage.setItem(
    ZOLA_PDP_INITIAL_COLOR_KEY,
    JSON.stringify({ ...previousStoredColors, ...newStoredColors })
  );
};

/**
 * Gets a saved theme color (associated to a suite UUID) from session storage, then
 * clears all stored colors (even if the specified suite doesn't have a saved color)
 */
export const extractInitialColor = (suiteUUID: string) => {
  if (!window?.sessionStorage || !suiteUUID) return null;

  const initialColors = JSON.parse(
    window.sessionStorage.getItem(ZOLA_PDP_INITIAL_COLOR_KEY) || '{}'
  );
  // Remove it after parsing the stored object
  window.sessionStorage.removeItem(ZOLA_PDP_INITIAL_COLOR_KEY);

  return initialColors[suiteUUID] || null;
};

/**
 * Stores a shape (silhouette) in session storage.
 * Used to select a previously filtered shape when a card PDP is loaded.
 *
 * @param shape The shape to select when loading a card PDP.
 */
export const storeInitialShape = (shape: string) => {
  if (!window?.sessionStorage || !shape) return;
  window.sessionStorage.setItem(ZOLA_PDP_FILTERED_SHAPE_PREVIEW, shape);
};

/**
 * Gets a previously saved card shape (silhouette) from session storage, then clears the value.
 */
export const extractInitialShape = () => {
  if (!window?.sessionStorage) return null;

  const initialShape = window.sessionStorage.getItem(ZOLA_PDP_FILTERED_SHAPE_PREVIEW);
  // Remove it after parsing the stored object
  window.sessionStorage.removeItem(ZOLA_PDP_FILTERED_SHAPE_PREVIEW);

  return initialShape || null;
};
